import { DragEvent, useContext, useEffect, useRef, useState } from 'react';
import type { Node as PMNode } from '@tiptap/pm/model';
import { NodeSelection } from '@tiptap/pm/state';
import type { EditorView } from '@tiptap/pm/view';
import { Button, Popover, PopoverSurface, Tooltip } from '@fluentui/react-components';
import { AddRegular } from '@fluentui/react-icons';

import DragHandle from '@shared/components/tables/DragHandle';

import DocumentContext from '../../contexts/DocumentContext.context';
import useEditorActive from '../../hooks/useEditorActive.hook';
import { findBodyBlock, isBodyBlockParent } from '../../tiptap/nodes/formField/formFieldHelpers';
import { FORM_FIELD_NODE_NAME } from '../../tiptap/nodes/formField/constants';
import { TITLE_NODE_NAME } from '../../tiptap/nodes/title/constants';
import {
  GUTTER_BUTTON_HEIGHT_PX,
  GUTTER_CARD_LABEL_OFFSET_PX,
  GUTTER_FIRST_LINE_MAX_PX,
  GUTTER_LEFT_OFFSET_PX,
} from './constants';
import InsertFieldMenu from './InsertFieldMenu';

type THoveredBlock = { pos: number; top: number; left: number; hasHandle: boolean };

type TMenu = { target: HTMLElement; blockPos: number };

// `inside` is the innermost node under the pointer: the body block itself, or something in it.
const findBlockAtCoords = (doc: PMNode, { pos, inside }: { pos: number; inside: number }) => {
  const node = inside >= 0 ? doc.nodeAt(inside) : null;
  if (node && isBodyBlockParent(doc.resolve(inside).parent) && !isBodyBlockParent(node)) return { node, pos: inside };
  return findBodyBlock(doc.resolve(pos));
};

// Placed from the block's own box, as a landscape section's text starts further left than the page's.
const findHoveredBlock = (view: EditorView, gutter: DOMRect, x: number, y: number): THoveredBlock | undefined => {
  const { left, right } = view.dom.getBoundingClientRect();
  // Clamped into the content, so a pointer in the margin finds the block beside it.
  const coords = view.posAtCoords({ left: Math.min(Math.max(x, left + 1), right - 1), top: y });
  const block = coords && findBlockAtCoords(view.state.doc, coords);
  const dom = block && view.nodeDOM(block.pos);
  if (!block || !(dom instanceof HTMLElement)) return undefined;

  const rect = dom.getBoundingClientRect();
  const isCard = block.node.type.name === FORM_FIELD_NODE_NAME;
  const offset = isCard
    ? GUTTER_CARD_LABEL_OFFSET_PX
    : (Math.min(rect.height, GUTTER_FIRST_LINE_MAX_PX) - GUTTER_BUTTON_HEIGHT_PX) / 2;
  // The title stays first.
  return {
    pos: block.pos,
    top: rect.top - gutter.top + offset,
    left: rect.left - gutter.left - GUTTER_LEFT_OFFSET_PX,
    hasHandle: block.node.type.name !== TITLE_NODE_NAME,
  };
};

// Starts the drag as ProseMirror starts its own: its drop moves the block, or copies it with the copy modifier.
const startBlockDrag = (view: EditorView, event: DragEvent, pos: number) => {
  const selection = NodeSelection.create(view.state.doc, pos);
  // The editor's own clipboard output, which carries what a plain serializer would lose.
  const { dom, text, slice } = view.serializeForClipboard(selection.content());
  event.dataTransfer.clearData();
  event.dataTransfer.setData('text/html', dom.innerHTML);
  event.dataTransfer.setData('text/plain', text);
  event.dataTransfer.effectAllowed = 'copyMove';
  const blockDom = view.nodeDOM(pos);
  if (blockDom instanceof HTMLElement) event.dataTransfer.setDragImage(blockDom, 0, 0);
  // ProseMirror's own drags carry the selection as `node`, which a move's drop deletes; the public type leaves it out.
  view.dragging = { slice, move: true, node: selection } as EditorView['dragging'];
  view.dispatch(view.state.tr.setSelection(selection));
};

const FormFieldGutter = () => {
  const { editor } = useContext(DocumentContext);
  const isEditable = useEditorActive((current) => current.isEditable);
  const refGutter = useRef<HTMLDivElement>(null);
  const [hovered, setHovered] = useState<THoveredBlock>();
  const [menu, setMenu] = useState<TMenu>();

  // One position lookup per frame while the pointer moves; held on its block while that block's menu is open.
  useEffect(() => {
    const gutter = refGutter.current;
    const paper = gutter?.parentElement;
    if (!gutter || !paper || !isEditable || menu) return;

    let frame = 0;
    let pointer = { x: 0, y: 0 };
    const onMove = (event: MouseEvent) => {
      pointer = { x: event.clientX, y: event.clientY };
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        setHovered(findHoveredBlock(editor.view, gutter.getBoundingClientRect(), pointer.x, pointer.y));
      });
    };
    const onLeave = () => setHovered(undefined);
    paper.addEventListener('mousemove', onMove);
    paper.addEventListener('mouseleave', onLeave);
    return () => {
      cancelAnimationFrame(frame);
      paper.removeEventListener('mousemove', onMove);
      paper.removeEventListener('mouseleave', onLeave);
    };
  }, [isEditable, !!menu]);

  return (
    <div ref={refGutter} className="relative h-0">
      {isEditable && hovered && (
        <div className="absolute z-10 flex items-center" style={{ top: hovered.top, left: hovered.left }}>
          <Tooltip content="Add a field or text" relationship="label" withArrow>
            <Button
              appearance="subtle"
              size="small"
              icon={<AddRegular />}
              onClick={(event) => setMenu({ target: event.currentTarget, blockPos: hovered.pos })}
            />
          </Tooltip>
          {hovered.hasHandle && (
            <div
              draggable
              className="grid h-6 w-5 cursor-grab place-items-center rounded text-base text-neutralFg-3 hover:bg-neutralBg-4"
              onDragStart={(event) => startBlockDrag(editor.view, event, hovered.pos)}
              onDragEnd={() => {
                editor.view.dragging = null;
              }}
            >
              <DragHandle />
            </div>
          )}
        </div>
      )}
      <Popover
        open={!!menu}
        onOpenChange={(_, data) => {
          if (!data.open) setMenu(undefined);
        }}
        positioning={{ target: menu?.target, position: 'below', align: 'start', offset: 4 }}
      >
        <PopoverSurface className="flex w-80 flex-col !p-1.5">
          {menu && <InsertFieldMenu blockPos={menu.blockPos} onDone={() => setMenu(undefined)} />}
        </PopoverSurface>
      </Popover>
    </div>
  );
};

export default FormFieldGutter;
